import { NgxSpinnerService } from "ngx-spinner";
import { TestResultDto } from "../dto/test-result-dto";
import { ClozeTestService } from "../service/cloze-test.service";
import { TestAnswer } from "../dto/test-answer";
import { AnswerDto } from "../dto/answer-dto";
import { ExerciseModel } from "../model/exercise-model";
import { BookingModel } from "../model/booking-model";
import { Component, OnInit, HostListener, input, output, signal, WritableSignal, inject } from "@angular/core";
import { ClozeTestStyleModel } from "../model/cloze-test-style-model";
import { ClozeTestModel } from "../model/cloze-test-model";
import { StorageService, StorageType } from "gast-common-ng2";
import { ExerciseComponent } from "../exercise";
import { ExerciseExampleComponent } from "../exercise-example";
import { IntroductionComponent } from "../introduction/introduction.component";
import { TestResultComponent } from "../test-result/test-result.component";
import { GerInfoComponent } from "../ger-info/ger-info.component";

@Component({
  selector: "onset-cloze-test",
  templateUrl: "./cloze-test.component.html",
  styleUrls: ["./cloze-test.component.scss"],
  standalone: true,
  imports: [ExerciseComponent, ExerciseExampleComponent, IntroductionComponent, TestResultComponent, GerInfoComponent],
})
export class ClozeTestComponent implements OnInit {
  readonly MOBILE_INSTRUCTION_TITLE = "Hinweise zum Test";

  /**
   * Option to show result at the end of the test
   * Default is false
   */
  readonly clozeTestService = inject(ClozeTestService);
  readonly spinnerService = inject(NgxSpinnerService);
  readonly storageService = inject(StorageService);

  readonly mobileMode = input(false);
  readonly productId = input(3);
  readonly showResult = input(false);
  readonly skipIntroExercise = input(false);
  readonly clozeTestModel = input({} as ClozeTestModel);
  readonly clozeTestStyleModel = input({} as ClozeTestStyleModel);
  readonly results = input({} as TestResultDto);
  readonly showEndTestButton = input(true);
  readonly lang = input("de");
  readonly showGer = input(true);
  readonly showCustomButton = input(false);
  readonly customButtonIcon = input("");
  readonly customButtonIconHeight = input("");
  readonly customButtonText = input("");
  readonly customResultLevelText = input("");
  readonly showIntroduction = input(false);
  readonly gerInfoNewTab = input(false);
  readonly currentResults: WritableSignal<TestResultDto> = signal({} as TestResultDto);
  startPageSubtitle = "";
  showGerInfo = false;

  readonly bookingInitialized = output<number>();
  readonly testSaved = output<number>();
  readonly startTest = output<boolean>();
  readonly testResult = output<TestResultDto>();
  readonly clozeTest = output<boolean>();
  readonly customButton = output<boolean>();
  readonly gerInfoClick = output<void>();

  booking!: BookingModel;

  startPageTitle!: string;
  startPageContent!: string;

  exerciseTitle!: string;
  exerciseContent!: string;
  exerciseId!: number;
  currentExercise = 0;
  lastExcercise!: number;
  isLastExercise = false;
  cTestLogo!: string;
  cTestButtonColor!: string;

  isTestStarted = false;
  isTestExampleStarted = false;
  isTestOver = false;

  testAnswer: TestAnswer = { CTestAntwort: [] } as TestAnswer;

  ngOnInit() {
    this.currentResults.set(this.results());
    if (this.clozeTestModel().booking) {
      this.booking = this.clozeTestModel().booking;
      this.#initiateBooking();
    } else {
      this.spinnerService.show();
      if (this.clozeTestModel().startWithBuchungId) {
        // If buchungId is definied then get next exercise
        this.#continueTest(this.showIntroduction());
      } else {
        // If not then start new test
        this.clozeTestService
          .getTest(this.clozeTestModel().getTestUrl, this.clozeTestModel().urlParameters)
          .subscribe((test) => {
            this.booking = test as BookingModel;
            this.#initiateBooking();
            this.spinnerService.hide();
          });
      }
    }
    this.#findLogo();
  }

  @HostListener("window:beforeunload")
  unloadHandler() {
    if (this.booking && this.booking.buchungId) {
      this.storageService.setItem("BrowserRefreshed", this.booking.buchungId, StorageType.Session);
    }
  }

  public onNextExercise(answers: AnswerDto[]) {
    this.testAnswer.CTestAntwort = answers;
    this.clozeTestService.postTest(this.clozeTestModel().postTestUrl, this.testAnswer.CTestAntwort).subscribe(() => {
      this.testAnswer = {
        CTestAntwort: [],
      };
      // if current exercise pos same like last available exercise pos get next exercise
      if (this.currentExercise === this.booking.aufgaben[this.booking.aufgaben.length - 1].position) {
        this.clozeTestService
          .getNextTest(
            this.clozeTestModel().getNextTestUrl,
            this.booking?.buchungId,
            this.clozeTestModel().urlParameters.sprache,
            this.clozeTestModel().urlParameters.start_level,
            this.clozeTestModel().urlParameters.end_level,
            this.clozeTestModel().urlParameters.variante
          )
          .subscribe((test) => {
            // end Test if exercise pos equals to number of exercise and is done
            const lastExerciseInList = (test as BookingModel).aufgaben[(test as BookingModel).aufgaben.length - 1];
            if (lastExerciseInList.position === this.booking.numAufgaben && lastExerciseInList.done) {
              this.onTestEnd(answers);
            } else {
              this.booking.aufgaben = this.booking.aufgaben.concat((test as BookingModel).aufgaben);
              this.spinnerService.hide();
              if (this.currentExercise < this.booking.numAufgaben) {
                this.currentExercise++;
                this.#prepareNextExercise();
              }
              if (this.currentExercise === this.booking.numAufgaben) {
                this.isLastExercise = true;
              }
            }
            this.testSaved.emit(this.booking?.buchungId);
          });
      } else {
        if (this.currentExercise < this.booking.numAufgaben) {
          this.currentExercise++;
          this.#prepareNextExercise();
        }
        if (this.currentExercise === this.booking.numAufgaben) {
          this.isLastExercise = true;
          this.testSaved.emit(this.booking?.buchungId);
        }
      }
      console.log("(onNextExercise) current exercise number: " + this.currentExercise);
    });
  }

  public onTestEnd(answers: AnswerDto[]): void {
    this.spinnerService.show();
    this.testAnswer.CTestAntwort = answers;
    this.clozeTestService.postTest(this.clozeTestModel().postTestUrl, this.testAnswer.CTestAntwort).subscribe(() => {
      this.clozeTestService
        .getResults(
          this.clozeTestModel().getResultsUrl,
          this.booking?.buchungId,
          this.clozeTestModel().urlParameters,
          this.clozeTestModel().frontEndLanguageId
        )
        .subscribe((testResult) => {
          this.isTestOver = true;
          this.currentResults.set(testResult as TestResultDto);
          this.testResult.emit(testResult as TestResultDto);
          this.spinnerService.hide();
          this.clozeTestService.updateResultShownTimeOfTest(
            this.clozeTestModel().onsetServiceUrl,
            this.booking?.buchungId
          );
          console.log(`update result shown time boooking id: ${this.booking?.buchungId}`);
        });
    });
  }

  public onStartTest(start: boolean) {
    this.isTestStarted = true;
    this.#prepareNextExercise();
    this.startTest.emit(start);
  }

  public onStartTestExample() {
    this.isTestExampleStarted = true;
  }

  public onCustomButton() {
    this.customButton.emit(true);
  }

  public onClozeTest(cloze: boolean): void {
    this.clozeTest.emit(cloze);
  }

  public onGerInfoClick(): void {
    this.showGerInfo = true;
    this.gerInfoClick.emit();
  }

  #initiateBooking(): void {
    const bookingIdBrowserRefreshed = this.storageService.getItem("BrowserRefreshed", StorageType.Session);
    if (bookingIdBrowserRefreshed) {
      console.log("Browser refresh detected, updating count");
      this.clozeTestService.updateBrowserRefreshCountOfTest(
        this.clozeTestModel().onsetServiceUrl,
        bookingIdBrowserRefreshed as number
      );
      console.log("remove Browser refresh from storage");
      this.storageService.removeItem("BrowserRefreshed", StorageType.Session);
    }
    this.bookingInitialized.emit(this.booking?.buchungId);
    this.booking.aufgaben.sort((a, b) => a.position - b.position);
    this.lastExcercise = this.booking.numAufgaben;
    if (this.skipIntroExercise()) {
      this.#findFirstNotFinishedExercise();
      this.#prepareNextExercise();
    } else {
      this.#prepareStartPage(this.booking.aufgaben.find((e) => e.id === 0 && e.position === 0) as ExerciseModel);
    }
  }

  #prepareStartPage(exercise: ExerciseModel) {
    if (this.mobileMode()) {
      this.startPageTitle = this.MOBILE_INSTRUCTION_TITLE;
      // NOTE: Mobile Content is in template
    } else {
      this.startPageTitle = exercise?.title;
      this.startPageContent = exercise?.content;
    }
    // TODO: prepare subtitle as well
  }

  #prepareNextExercise(): void {
    const exercise = this.booking.aufgaben.find((a) => a.position === this.currentExercise && !a.done);
    if (exercise) {
      this.exerciseId = exercise.id;
      this.exerciseTitle = exercise.title;
      this.exerciseContent = exercise.content;
    } else {
      this.spinnerService.show();
      this.clozeTestService
        .getResults(
          this.clozeTestModel().getResultsUrl,
          this.booking?.buchungId,
          this.clozeTestModel().urlParameters,
          this.clozeTestModel().frontEndLanguageId
        )
        .subscribe((testResult) => {
          this.isTestOver = true;
          this.currentResults.set(testResult as TestResultDto);
          this.testResult.emit(testResult as TestResultDto);
          this.spinnerService.hide();
        });
    }
  }

  #continueTest(showIntroduction: boolean): void {
    this.clozeTestService
      .getNextTest(
        this.clozeTestModel().getNextTestUrl,
        this.clozeTestModel().startWithBuchungId,
        this.clozeTestModel().urlParameters.sprache,
        this.clozeTestModel().urlParameters.start_level,
        this.clozeTestModel().urlParameters.end_level,
        this.clozeTestModel().urlParameters.variante
      )
      .subscribe((test) => {
        this.isTestStarted = !showIntroduction;
        this.booking = test as BookingModel;
        this.#addManuallyStartPage();
        this.#initiateBooking();
        this.#findFirstNotFinishedExercise();
        this.#prepareNextExercise();
        this.spinnerService.hide();
      });
  }

  #findFirstNotFinishedExercise(): void {
    let lastDone = 0;
    // NOTE:this.booking.aufgaben is already sorted by position
    // position indicated the exercise index in the current booking
    // find first unfinished exercise and use the position as the current index
    for (const exercise of this.booking.aufgaben) {
      if (!exercise.done && exercise.id !== 0) {
        this.currentExercise = exercise.position;
        break;
      }
      if (exercise.done) {
        lastDone = exercise.position;
      }
    }
    // if current exercise equals to total exercise set last exercise to true
    if (this.currentExercise === this.booking.numAufgaben) {
      this.isLastExercise = true;
    }
    if (lastDone === this.booking.numAufgaben) {
      this.currentExercise = lastDone;
      console.log("test is done");
    }
    console.log("(findFirstNotFinishedExercise) current exercise number: " + this.currentExercise);
  }

  #addManuallyStartPage(): void {
    let isDone = true;
    if (this.showIntroduction()) {
      isDone = false;
    }
    const startExercise = { done: isDone, position: 0, id: 0 } as ExerciseModel;
    this.booking.aufgaben.unshift(startExercise);
  }

  #findLogo() {
    if (this.productId() === 0) {
      // Allgemein
      this.cTestLogo = "onset-assets/img/testdaf_logo.png";
      this.cTestButtonColor = this.clozeTestStyleModel().testdafColor;
    } else if (this.productId() === 1) {
      // TestDaF
      this.cTestLogo = "onset-assets/img/testdaf_logo.png";
      this.cTestButtonColor = this.clozeTestStyleModel().testdafColor;
    } else if (this.productId() === 2) {
      // TestAS
      this.cTestLogo = "onset-assets/img/testas_logo.png";
      this.cTestButtonColor = this.clozeTestStyleModel().testasColor;
    } else if (this.productId() === 3) {
      // onSET
      this.cTestLogo = "onset-assets/img/onset_logo.png";
      this.cTestButtonColor = this.clozeTestStyleModel().onsetColor;
    } else if (this.productId() === 4) {
      // DUO
      this.cTestLogo = "onset-assets/img/duo_logo_new.png";
      this.cTestButtonColor = this.clozeTestStyleModel().duoColor;
    } else if (this.productId() === 5) {
      // APS
      this.cTestLogo = "onset-assets/img/testdaf_logo.png";
      this.cTestButtonColor = this.clozeTestStyleModel().testdafColor;
    } else if (this.productId() === 6) {
      // Sprachnachweis
      this.cTestLogo = "onset-assets/img/testdaf_logo.png";
      this.cTestButtonColor = this.clozeTestStyleModel().testdafColor;
    } else {
      this.cTestLogo = "onset-assets/img/testdaf_logo.png";
      this.cTestButtonColor = this.clozeTestStyleModel().testdafColor;
    }
  }
}
